<template>
    <div class="w-100">
        <v-row no-gutters>
            <v-col cols="12">
                <v-expansion-panels class="w-100">
                    <v-expansion-panel>
                        <v-expansion-panel-title class="w-100">
                            <template v-slot:default="{ expanded }">
                                <v-row no-gutters>
                                    <v-col cols="10"> Gestion des chronos </v-col>
                                    <v-col cols="2" v-if="expanded">
                                        <v-tooltip location="bottom">
                                            <template v-slot:activator="{ props }">
                                                <v-icon
                                                    color="secondary"
                                                    v-bind="props"
                                                    class="pr-4 w-100 h-100"
                                                    >$oss-help</v-icon
                                                >
                                            </template>
                                            <span>
                                                <strong>
                                                    Formatage du chrono de facturation
                                                </strong>
                                                <br />
                                            </span>
                                            <span>
                                                Ce qui doit être présent obligatoirement:
                                                <br />
                                            </span>
                                            <span>- Le compteur de factures = "X"<br /><br /></span>
                                            <span>Ce que vous pouvez saisir:<br /></span>
                                            <span>- Année au format long = "YYYY"<br /></span>
                                            <span>- Année au format court = "YY"<br /></span>
                                            <span>- Mois = "MM" pour afficher le mois<br /></span>
                                            <span>- Jour = "DD" pour afficher le jour<br /></span>
                                        </v-tooltip>
                                    </v-col>
                                </v-row>
                            </template>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <InvoiceChronoSetting
                                :companyId="companyId"
                                :chrono="advanceSetting.chrono"
                            >
                            </InvoiceChronoSetting>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    <v-expansion-panel>
                        <v-expansion-panel-title data-cy="invoice-settings-email">
                            Gestion des mails
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <InvoiceMailSetting
                                :defaultMails="advanceSetting.defaultMail"
                                @mailTemplateDelete="onMailTemplateDeleted"
                            />
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
        </v-row>
        <v-row no-gutters class="pb-3 pt-2">
            <v-col cols="9" />
            <v-spacer />
            <v-col cols="3" class="d-flex justify-end">
                <v-btn
                    data-cy="invoice-settings-save"
                    rounded
                    @click="updateAdvanceSettings()"
                    size="small"
                    >Enregister</v-btn
                >
            </v-col>
        </v-row>
    </div>
</template>

<script lang="ts">
import { defaultInvoiceMail, invoiceAdvanceSettings } from "@/types/invoiceAdvanceSettings.types";

import invoiceAdvanceSettingsDefaultScheme from "@/assets/json/invoiceAdvanceSettingsDefaultScheme.json";
import invoiceDefaultMailScheme from "@/assets/json/invoiceDefaultMailScheme.json";
import { Apollo } from "@/decorators";

import InvoiceChronoSetting from "./InvoiceChronoSetting.vue";
import InvoiceMailSetting from "./InvoiceMailSetting.vue";

import GET_INVOICE_DATA from "@/graphql/company/GetCompanyInvoiceData.gql";
import UPDATE_COMPANY_CHRONO from "@/graphql/company/UpdateCompanyInvoiceData.gql";
import { Component, Vue, toNative, Prop } from "vue-facing-decorator";
import { MailTemplate } from "@/store/store";

@Component({
    components: {
        InvoiceChronoSetting,
        InvoiceMailSetting,
    },
})
export class InvoiceAdvanceSetting extends Vue {
    error: string = "";

    @Prop({ default: "" }) companyId: string;

    // Computed function: Fetch mail templates from Vuex store
    get mailTemplates(): MailTemplate[] {
        return this.$store.state.invoiceMailTemplates;
    }

    // Récupérer selectedTemplateName depuis le store
    get selectedTemplateName(): string {
        return this.$store.state.selectedInvoiceMailTemplateName;
    }

    @Apollo({
        query: GET_INVOICE_DATA,
        variables() {
            return {
                companyId: this.companyId ? this.companyId : this.$store.state.selected_company.id,
            };
        },
        skip() {
            return false;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                const info = data.company;
                this.$store.state.invoiceMailTemplates =
                    info.invoiceData?.mail?.invoice.templates || [];
                this.$store.state.selectedInvoiceMailTemplateName =
                    info.invoiceData?.mail?.invoice.selectedTemplateName || "default";

                this.advanceSetting.chrono.invoiceChrono = info.invoiceData?.chrono?.invoiceChrono
                    ? info.invoiceData.chrono.invoiceChrono
                    : "FACTURE-YYYYMM-XXXXXXX";
                this.advanceSetting.chrono.quoteChrono = info.invoiceData?.chrono?.quoteChrono
                    ? info.invoiceData.chrono.quoteChrono
                    : "DEVIS-YYYYMM-XXXXXXX";
                this.advanceSetting.chrono.deliveryBillChrono = info.invoiceData?.chrono
                    ?.deliveryBillChrono
                    ? info.invoiceData.chrono.deliveryBillChrono
                    : "BL-YYYYMM-XXXXXXX";
                this.advanceSetting.chrono.creditNoteChrono = info.invoiceData?.chrono
                    ?.creditNoteChrono
                    ? info.invoiceData.chrono.creditNoteChrono
                    : "AVOIR-YYYYMM-XXXXXXX";
                this.advanceSetting.chrono.depositChrono = info.invoiceData?.chrono?.depositChrono
                    ? info.invoiceData.chrono.depositChrono
                    : "AC-YYYYMM-XXXXXXX";

                this.advanceSetting.chrono.invoiceResetCounterCondition = info.invoiceData?.chrono
                    ?.invoiceResetCounterCondition
                    ? info.invoiceData.chrono.invoiceResetCounterCondition
                    : "NONE";
                this.advanceSetting.chrono.quoteResetCounterCondition = info.invoiceData?.chrono
                    ?.quoteResetCounterCondition
                    ? info.invoiceData.chrono.quoteResetCounterCondition
                    : "NONE";
                this.advanceSetting.chrono.deliveryBillResetCounterCondition = info.invoiceData
                    ?.chrono?.deliveryBillResetCounterCondition
                    ? info.invoiceData.chrono.deliveryBillResetCounterCondition
                    : "NONE";
                this.advanceSetting.chrono.creditNoteResetCounterCondition = info.invoiceData
                    ?.chrono?.creditNoteResetCounterCondition
                    ? info.invoiceData.chrono.creditNoteResetCounterCondition
                    : "NONE";
                this.advanceSetting.chrono.depositResetCounterCondition = info.invoiceData?.chrono
                    ?.depositResetCounterCondition
                    ? info.invoiceData.chrono.depositResetCounterCondition
                    : "NONE";

                this.advanceSetting.counter.invoice = info.invoiceData?.counter?.invoice
                    ? info.invoiceData.counter.invoice
                    : {};
                this.advanceSetting.counter.quote = info.invoiceData?.counter?.quote
                    ? info.invoiceData.counter.quote
                    : {};
                this.advanceSetting.counter.deliveryBill = info.invoiceData?.counter?.deliveryBill
                    ? info.invoiceData.counter.deliveryBill
                    : {};
                this.advanceSetting.counter.creditNote = info.invoiceData?.counter?.creditNote
                    ? info.invoiceData.counter.creditNote
                    : {};
                this.advanceSetting.counter.deposit = info.invoiceData?.counter?.deposit
                    ? info.invoiceData.counter.deposit
                    : {};

                this.advanceSetting.defaultMail.invoice.header = info.invoiceData?.mail?.invoice
                    ?.header
                    ? info.invoiceData.mail.invoice.header
                    : invoiceDefaultMailScheme.invoice.header;
                this.advanceSetting.defaultMail.invoice.body = info.invoiceData?.mail?.invoice?.body
                    ? info.invoiceData.mail.invoice.body
                    : invoiceDefaultMailScheme.invoice.body;

                this.advanceSetting.defaultMail.invoice.selectedTemplateName =
                    this.selectedTemplateName;
                this.advanceSetting.defaultMail.invoice.templates = this.mailTemplates;

                this.advanceSetting.defaultMail.quote.header = info.invoiceData?.mail?.quote?.header
                    ? info.invoiceData.mail.quote.header
                    : invoiceDefaultMailScheme.quote.header;
                this.advanceSetting.defaultMail.quote.body = info.invoiceData?.mail?.quote?.body
                    ? info.invoiceData.mail.quote.body
                    : invoiceDefaultMailScheme.quote.body;

                this.advanceSetting.defaultMail.creditNote.header = info.invoiceData?.mail
                    ?.creditNote?.header
                    ? info.invoiceData.mail.creditNote.header
                    : invoiceDefaultMailScheme.creditNote.header;
                this.advanceSetting.defaultMail.creditNote.body = info.invoiceData?.mail?.creditNote
                    ?.body
                    ? info.invoiceData.mail.creditNote.body
                    : invoiceDefaultMailScheme.creditNote.body;

                this.advanceSetting.defaultMail.deliveryBill.header = info.invoiceData?.mail
                    ?.deliveryBill?.header
                    ? info.invoiceData.mail.deliveryBill.header
                    : invoiceDefaultMailScheme.deliveryBill.header;
                this.advanceSetting.defaultMail.deliveryBill.body = info.invoiceData?.mail
                    ?.deliveryBill?.body
                    ? info.invoiceData.mail.deliveryBill.body
                    : invoiceDefaultMailScheme.deliveryBill.body;

                this.advanceSetting.defaultMail.deposit.header = info.invoiceData?.mail?.deposit
                    ?.header
                    ? info.invoiceData.mail.deposit.header
                    : invoiceDefaultMailScheme.deposit.header;
                this.advanceSetting.defaultMail.deposit.body = info.invoiceData?.mail?.deposit?.body
                    ? info.invoiceData.mail.deposit.body
                    : invoiceDefaultMailScheme.deposit.body;

                this.advanceSetting.defaultMail.reminder1.header = info.invoiceData?.mail?.reminder1
                    ?.header
                    ? info.invoiceData.mail.reminder1.header
                    : invoiceDefaultMailScheme.reminder1.header;
                this.advanceSetting.defaultMail.reminder1.body = info.invoiceData?.mail?.reminder1
                    ?.body
                    ? info.invoiceData.mail.reminder1.body
                    : invoiceDefaultMailScheme.reminder1.body;

                this.advanceSetting.defaultMail.reminder2.header = info.invoiceData?.mail?.reminder2
                    ?.header
                    ? info.invoiceData.mail.reminder2.header
                    : invoiceDefaultMailScheme.reminder2.header;
                this.advanceSetting.defaultMail.reminder2.body = info.invoiceData?.mail?.reminder2
                    ?.body
                    ? info.invoiceData.mail.reminder2.body
                    : invoiceDefaultMailScheme.reminder2.body;

                this.advanceSetting.defaultMail.reminder3.header = info.invoiceData?.mail?.reminder3
                    ?.header
                    ? info.invoiceData.mail.reminder3.header
                    : invoiceDefaultMailScheme.reminder3.header;
                this.advanceSetting.defaultMail.reminder3.body = info.invoiceData?.mail?.reminder3
                    ?.body
                    ? info.invoiceData.mail.reminder3.body
                    : invoiceDefaultMailScheme.reminder3.body;

                this.advanceSetting.defaultMail.delay.header = info.invoiceData?.mail?.delay?.header
                    ? info.invoiceData.mail.delay.header
                    : invoiceDefaultMailScheme.delay.header;
                this.advanceSetting.defaultMail.delay.upperBody = info.invoiceData?.mail?.delay
                    ?.upperBody
                    ? info.invoiceData.mail.delay.upperBody
                    : invoiceDefaultMailScheme.delay.upperBody;
                this.advanceSetting.defaultMail.delay.lineBody = info.invoiceData?.mail?.delay
                    ?.lineBody
                    ? info.invoiceData.mail.delay.lineBody
                    : invoiceDefaultMailScheme.delay.lineBody;
                this.advanceSetting.defaultMail.delay.lowerBody = info.invoiceData?.mail?.delay
                    ?.lowerBody
                    ? info.invoiceData.mail.delay.lowerBody
                    : invoiceDefaultMailScheme.delay.lowerBody;

                this.advanceSetting.defaultMail.multipleReminder.header = info.invoiceData?.mail
                    ?.multipleReminder?.header
                    ? info.invoiceData.mail.multipleReminder.header
                    : invoiceDefaultMailScheme.multipleReminder.header;
                this.advanceSetting.defaultMail.multipleReminder.upperBody = info.invoiceData?.mail
                    ?.multipleReminder?.upperBody
                    ? info.invoiceData.mail.multipleReminder.upperBody
                    : invoiceDefaultMailScheme.multipleReminder.upperBody;
                this.advanceSetting.defaultMail.multipleReminder.lowerBody = info.invoiceData?.mail
                    ?.multipleReminder?.lowerBody
                    ? info.invoiceData.mail.multipleReminder.lowerBody
                    : invoiceDefaultMailScheme.multipleReminder.lowerBody;
                this.advanceSetting.defaultMail.multipleReminder.lineBody = info.invoiceData?.mail
                    ?.multipleReminder?.lineBody
                    ? info.invoiceData.mail.multipleReminder.lineBody
                    : invoiceDefaultMailScheme.multipleReminder.lineBody;

                this.advanceSetting.defaultMail.signature = info.invoiceData?.mail?.signature
                    ? info.invoiceData.mail.signature
                    : "$COMPANY_NAME\n$USER_NAME";
            }
        },
    })
    advanceSetting: invoiceAdvanceSettings = invoiceAdvanceSettingsDefaultScheme;

    async updateSelectedMailTemplate(mail: defaultInvoiceMail) {
        const selectedMailTemplate = this.mailTemplates.find(
            (template) => template.value === this.selectedTemplateName,
        );
        const mailTemplateToUpdate: MailTemplate = {
            title: selectedMailTemplate.title,
            value: selectedMailTemplate.value,
            header: mail.invoice.header,
            body: mail.invoice.body,
            signature: mail.signature,
        };
        this.$store.dispatch("updateInvoiceMailTemplate", mailTemplateToUpdate);
    }

    async updateAdvanceSettings() {
        const chrono = this.advanceSetting.chrono;
        const mail = this.advanceSetting.defaultMail;
        if (this.mailTemplates.length) {
            await this.updateSelectedMailTemplate(mail);
        }
        if (
            chrono.error.invoiceChrono.length ||
            chrono.error.quoteChrono.length ||
            chrono.error.deliveryBillChrono.length ||
            chrono.error.creditNoteChrono.length ||
            chrono.error.depositChrono.length
        ) {
            return "";
        } else {
            const chronoJSON = {
                invoiceChrono: chrono.invoiceChrono,
                invoiceResetCounterCondition: chrono.invoiceResetCounterCondition,
                quoteChrono: chrono.quoteChrono,
                quoteResetCounterCondition: chrono.quoteResetCounterCondition,
                deliveryBillChrono: chrono.deliveryBillChrono,
                deliveryBillResetCounterCondition: chrono.deliveryBillResetCounterCondition,
                creditNoteChrono: chrono.creditNoteChrono,
                creditNoteResetCounterCondition: chrono.creditNoteResetCounterCondition,
                depositChrono: chrono.depositChrono,
                depositResetCounterCondition: chrono.depositResetCounterCondition,
            };

            const mailJSON = {
                invoice: {
                    body: mail.invoice.body,
                    header: mail.invoice.header,
                    selectedTemplateName: this.selectedTemplateName,
                    templates: this.mailTemplates,
                },
                quote: {
                    body: mail.quote.body,
                    header: mail.quote.header,
                },
                delay: {
                    upperBody: mail.delay.upperBody,
                    lineBody: mail.delay.lineBody,
                    lowerBody: mail.delay.lowerBody,
                    header: mail.delay.header,
                },
                creditNote: {
                    body: mail.creditNote.body,
                    header: mail.creditNote.header,
                },
                deliveryBill: {
                    body: mail.deliveryBill.body,
                    header: mail.deliveryBill.header,
                },
                deposit: {
                    body: mail.deposit.body,
                    header: mail.deposit.header,
                },
                reminder1: {
                    body: mail.reminder1.body,
                    header: mail.reminder1.header,
                },
                reminder2: {
                    body: mail.reminder2.body,
                    header: mail.reminder2.header,
                },
                reminder3: {
                    body: mail.reminder3.body,
                    header: mail.reminder3.header,
                },
                multipleReminder: {
                    upperBody: mail.multipleReminder.upperBody,
                    lowerBody: mail.multipleReminder.lowerBody,
                    lineBody: mail.multipleReminder.lineBody,
                    header: mail.multipleReminder.header,
                },
                signature: mail.signature,
            };
            await this.$apollo.mutate({
                mutation: UPDATE_COMPANY_CHRONO,
                variables: {
                    companyId: this.companyId ? this.companyId : this.$store.state.selected_company.id,
                    invoiceData: {
                        chrono: chronoJSON,
                        counter: this.advanceSetting.counter,
                        mail: mailJSON,
                    },
                },
            });
        }
    }

    onMailTemplateDeleted() {
        this.updateAdvanceSettings();
    }
}

export default toNative(InvoiceAdvanceSetting);
</script>
